import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import bankAccountService from '../../services/bankAccountService';

interface BankAdjustmentFormProps {
  bankAccountId: string;
  accountName: string;
  currentBalance: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 8,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
  colorScheme: 'light',
};

const BankAdjustmentForm: React.FC<BankAdjustmentFormProps> = ({
  bankAccountId,
  accountName,
  currentBalance,
  onClose,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    amount: '',
    type: 'ADD' as 'ADD' | 'REDUCE',
    date: new Date().toISOString().split('T')[0],
    description: '',
    reference: ''
  });
  const [error, setError] = useState('');

  const createAdjustmentMutation = useMutation({
    mutationFn: (data: any) => bankAccountService.createAdjustment(bankAccountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['bankAccountBalance', bankAccountId] });
      queryClient.invalidateQueries({ queryKey: ['bankAccountTransactions', bankAccountId] });
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to create adjustment');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Amount must be a positive number');
      return;
    }

    if (formData.type === 'REDUCE' && amount > currentBalance) {
      setError('Cannot reduce more than current balance');
      return;
    }

    createAdjustmentMutation.mutate({
      amount,
      type: formData.type,
      date: formData.date,
      description: formData.description,
      reference: formData.reference
    });
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const newBalance = formData.amount
    ? formData.type === 'ADD'
      ? currentBalance + parseFloat(formData.amount)
      : currentBalance - parseFloat(formData.amount)
    : currentBalance;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.72)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 60,
    }}>
      <div style={{
        background: '#ffffff',
        border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 6,
        maxWidth: 440,
        width: '100%',
        margin: '0 16px',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <div style={{ padding: 24 }}>
          {/* Modal Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 20,
                fontWeight: 300,
                color: '#1c2033',
                letterSpacing: '0.02em',
              }}>
                Bank Account Adjustment
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                Adjust the balance for this account
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6b6a5c',
                padding: 4,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Account Info Panel */}
          <div style={{
            marginBottom: 20,
            padding: 14,
            background: '#faf7ee',
            border: '1px solid rgba(92,78,51,0.15)',
            borderRadius: 4,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 4 }}>Account</div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033' }}>{accountName}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 4 }}>Current Balance</div>
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 20,
                  fontWeight: 300,
                  color: '#fbbf24',
                }}>
                  BD {currentBalance.toFixed(3)}
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Error */}
            {error && (
              <div style={{
                marginBottom: 16,
                padding: '10px 14px',
                background: 'rgba(201,90,90,0.08)',
                border: '1px solid rgba(201,90,90,0.25)',
                borderRadius: 4,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#c95a5a',
              }}>
                {error}
              </div>
            )}

            {/* Adjustment Type */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>
                Adjustment Type <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => handleChange('type', 'ADD')}
                  style={{
                    padding: '9px 16px',
                    borderRadius: 4,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    ...(formData.type === 'ADD'
                      ? {
                          background: 'rgba(90,201,140,0.15)',
                          border: '1px solid rgba(90,201,140,0.4)',
                          color: '#5ac98c',
                        }
                      : {
                          background: 'rgba(241,245,249,1)',
                          border: '1px solid rgba(92,78,51,0.22)',
                          color: '#6b6a5c',
                        }
                    ),
                  }}
                >
                  Add Money
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('type', 'REDUCE')}
                  style={{
                    padding: '9px 16px',
                    borderRadius: 4,
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    ...(formData.type === 'REDUCE'
                      ? {
                          background: 'rgba(201,90,90,0.15)',
                          border: '1px solid rgba(201,90,90,0.4)',
                          color: '#c95a5a',
                        }
                      : {
                          background: 'rgba(241,245,249,1)',
                          border: '1px solid rgba(92,78,51,0.22)',
                          color: '#6b6a5c',
                        }
                    ),
                  }}
                >
                  Reduce Money
                </button>
              </div>
            </div>

            {/* Amount */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                Amount (BD) <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <input
                type="number"
                step="0.001"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                style={inputStyle}
                placeholder="0.000"
                required
              />
            </div>

            {/* Date */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                Date <span style={{ color: '#c95a5a' }}>*</span>
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            {/* Reference */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Reference</label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => handleChange('reference', e.target.value)}
                style={inputStyle}
                placeholder="e.g., DEP-001, FEE-2024"
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: 72,
                }}
                rows={3}
                placeholder="e.g., Cash deposit, Bank fees, etc."
              />
            </div>

            {/* New Balance Preview */}
            {formData.amount && (
              <div style={{
                marginBottom: 20,
                padding: '12px 16px',
                background: '#faf7ee',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 4,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6b6a5c' }}>New Balance</span>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 20,
                      fontWeight: 300,
                      color: newBalance >= 0 ? '#fbbf24' : '#c95a5a',
                    }}>
                      BD {isNaN(newBalance) ? '0.000' : newBalance.toFixed(3)}
                    </span>
                    {formData.amount && !isNaN(parseFloat(formData.amount)) && (
                      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: formData.type === 'ADD' ? '#5ac98c' : '#c95a5a', marginTop: 2 }}>
                        {formData.type === 'ADD' ? '+' : '-'}BD {parseFloat(formData.amount).toFixed(3)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  background: 'transparent',
                  border: '1px solid rgba(92,78,51,0.35)',
                  borderRadius: 4,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#d97706',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createAdjustmentMutation.isPending}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  background: createAdjustmentMutation.isPending
                    ? 'rgba(217,119,6,0.25)'
                    : 'linear-gradient(135deg, #d97706, #b45309)',
                  border: 'none',
                  borderRadius: 4,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#1c2033',
                  cursor: createAdjustmentMutation.isPending ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  opacity: createAdjustmentMutation.isPending ? 0.7 : 1,
                }}
              >
                {createAdjustmentMutation.isPending
                  ? 'Processing...'
                  : formData.type === 'ADD'
                  ? 'Add Money'
                  : 'Reduce Money'
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BankAdjustmentForm;
